<script setup>
import { computed, defineProps, defineEmits, ref } from "vue";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import Divider from "primevue/divider";
import InputSwitch from "primevue/inputswitch";
import { useMapPrefs } from "@/composables/useMapPrefs";
import { useMapStore } from "@/store/map";
import { useApi } from "@/composables/useApi";
import { useScreenStore } from "@/store/useScreenStore";
import { useAppToast } from "@/composables/useAppToast";
import MapaBase from "@/components/MapaBase/MapaBase.vue";

defineProps({
  visible: Boolean,
});
const emit = defineEmits(["update:visible"]);

const { googleEnabled, setGoogleEnabled } = useMapPrefs();
const m = useMapStore();
const api = useApi();
const screen = useScreenStore();
const toast = useAppToast();

const usarGoogle = computed({
  get: () => googleEnabled.value,
  set: (val) => setGoogleEnabled(val),
});

const guardandoCentro = ref(false);
// Punto elegido en el mapa (buscando dirección o haciendo click). Se guarda en un
// ref propio en vez de leer m.center reactivamente, porque useMapStore() desestructura
// el ref del store una sola vez al crear cada instancia y no refleja cambios posteriores.
const centro = ref(m.getStoreCenter());

const onMapClick = (e) => {
  centro.value = { lat: e.latlng.lat, lng: e.latlng.lng };
};

const guardarCentro = async () => {
  guardandoCentro.value = true;
  try {
    m.setCenter(centro.value);
    await api.put("/api/config", { mapCenter: [centro.value.lat, centro.value.lng] });
    toast.success("Centro guardado", "El mapa se abrirá en este punto cuando no haya una posición guardada.");
  } catch (e) {
    toast.error("No se pudo guardar el centro del mapa");
  } finally {
    guardandoCentro.value = false;
  }
};
</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="(v) => emit('update:visible', v)"
    modal
    header="Mapa"
    :style="{ width: screen.isMobile ? '100%' : '520px' }"
  >
    <div class="text-sm text-500 mb-3">Usar el mapa de Google en todos los mapas de la aplicación</div>
    <div class="flex align-items-center gap-2">
      <InputSwitch v-model="usarGoogle" />
      <span class="switch-label">{{ usarGoogle ? 'Activado' : 'Desactivado' }}</span>
    </div>

    <Divider />

    <div class="section-title mb-1">Centro predeterminado</div>
    <div class="text-sm text-500 mb-2">
      Buscá una dirección o hacé click en el mapa para marcar el punto donde se abrirá el mapa por defecto.
    </div>
    <MapaBase
      :search="true"
      :setPoint="true"
      :saveCenter="false"
      height="300px"
      style="border-radius: var(--radius-md); overflow: hidden;"
      @mapClick="onMapClick"
    />
    <div class="flex align-items-center justify-content-between mt-2">
      <span class="text-xs text-500">
        Coordenadas: {{ centro.lat.toFixed(5) }}, {{ centro.lng.toFixed(5) }}
      </span>
      <Button
        label="Guardar centro"
        icon="pi pi-check"
        size="small"
        :loading="guardandoCentro"
        @click="guardarCentro"
      />
    </div>

    <template #footer>
      <Button label="Cerrar" text @click="emit('update:visible', false)" />
    </template>
  </Dialog>
</template>

<style scoped>
.switch-label {
  font-size: var(--text-callout);
  color: var(--label-secondary);
}

.section-title {
  font-weight: var(--weight-semibold);
  font-size: var(--text-callout);
  color: var(--label-primary);
}
</style>
